import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import FormField from '../../components/ui/FormField.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import BrandLogo from '../../components/BrandLogo.jsx';
import { useAuthStore } from '../../store/authStore.js';
import { toastError, toastWarning } from '../../hooks/useToast.js';
import SumCaptcha from '../../components/auth/SumCaptcha.jsx';

const inputLight =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const token = useAuthStore((s) => s.token);

  const from = location.state?.from?.pathname || '/admin/dashboard';

  useEffect(() => {
    let raw;
    try {
      raw = sessionStorage.getItem('admin_auth_notice');
    } catch {
      return;
    }
    if (!raw) return;
    try {
      sessionStorage.removeItem('admin_auth_notice');
    } catch {
      /* ignore */
    }
    try {
      const parsed = JSON.parse(raw);
      const title =
        typeof parsed?.title === 'string' && parsed.title.trim() !== ''
          ? parsed.title.trim()
          : 'Sesión no válida';
      const text =
        typeof parsed?.text === 'string' && parsed.text.trim() !== ''
          ? parsed.text
          : 'Vuelve a iniciar sesión para continuar en el panel.';
      toastWarning(title, text);
    } catch {
      toastWarning('Sesión no válida', 'Vuelve a iniciar sesión para continuar en el panel.');
    }
  }, []);

  useEffect(() => {
    if (token) navigate(from, { replace: true });
  }, [token, from, navigate]);

  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values) {
    if (!captchaValid) {
      toastError('Verificación requerida', 'Selecciona el resultado correcto de la suma.');
      return;
    }
    try {
      await login(values.email, values.password);
      navigate(from, { replace: true });
    } catch (e) {
      toastError('No se pudo iniciar sesión', e.message);
      setCaptchaAnswer('');
      setCaptchaValid(false);
    }
  }

  const loading = useAuthStore((s) => s.loading);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-page-dark bg-fixed px-4 py-16">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(103,178,53,0.15),transparent_45%)]" />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-16 w-44 items-center justify-center rounded-2xl border border-white/15 bg-slate-950/60 p-2 ring-1 ring-brand-navy/40 backdrop-blur-sm">
            <BrandLogo className="max-h-12" alt="Coonaltragas" />
          </div>
          <h1 className="mt-6 font-display text-2xl font-bold text-white md:text-3xl">Acceso administrativo</h1>
          <p className="mt-3 text-sm text-white/85">
            Gestión de contenidos del portal de empleados
          </p>
        </div>

        <PremiumCard variant="light">
          <form
            className="space-y-5 [&_label>span]:text-slate-600 [&_p.text-red-400]:text-red-600"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <FormField label="Correo" error={errors.email?.message}>
              <input
                autoComplete="email"
                placeholder="Correo electrónico"
                className={inputLight}
                {...register('email', { required: 'Requerido' })}
              />
            </FormField>
            <FormField label="Contraseña" error={errors.password?.message}>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Contraseña de acceso"
                  className={`${inputLight} pr-11`}
                  {...register('password', { required: 'Requerido' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-600"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                  )}
                </button>
              </div>
            </FormField>

            <SumCaptcha
              value={captchaAnswer}
              onChange={setCaptchaAnswer}
              onValidChange={setCaptchaValid}
            />

            <Button type="submit" disabled={loading || !captchaValid} className="w-full">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Entrando…
                </>
              ) : (
                'Iniciar sesión'
              )}
            </Button>
          </form>
        </PremiumCard>


      </div>
    </div>
  );
}
